import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import {
  CONFIRMATION_MACRO_TARGETS,
  QUESTION_RESTART_CURRENT_PROGRAMME,
  QUESTION_START_DATE,
  QUESTION_STOP_CURRENT_PROGRAMME,
} from '../../services/shared_programme_onboarding';
import SharedProgrammeOnboardingStepStartDate from './shared_programme_onboarding_step_start_date';
import {
  END_USER_PLANNER_RECOMMENDER_ROUTE,
  MY_DAY_ROUTE,
} from '../../services/routes';
import SharedProgrammeOnboardingStepConfirmationMacroTargets from './shared_programme_onboarding_step_confirmation_macro_targets';
import { deduplicate } from '../../services/arrays';
import { targetDayNutritionFromMetricConstraints } from '../../services/nutrition';
import {
  dayIndexForCalendarDate,
  viewWeekStartDateStr,
} from '../../services/planner';
import SharedProgrammeOnboardingStepStopCurrentProgramme from './shared_programme_onboarding_step_stop_current_programme';
import {
  useCreateUserProgrammeEnrollmentMutation,
  useDeleteContentEntryMutation,
  useDeleteMealMutation,
  useDeleteNoteMutation,
  useGetSharedProgrammeQuery,
  useUpdateDbPlannerWeekMutation,
  useUpdateUserProgrammeEnrollmentMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { findAnswerFor, QUESTION_GOAL } from '../../services/space_onboarding';
import { currentProgrammeTargetCalories } from '../../services/end_user_space_memberships';
import { sharedProgrammeDurationDays } from '../../services/programmes';
import EndUserSharedProgrammeRestartScreen from './end_user_shared_programme_restart_screen';
import { calendarDayFromJSDate } from '../../services/dates';
import {
  isProgrammeEnrollmentCompleted,
  programmeStartCalendarDay,
  programmeTotalDays,
} from '../../services/my_day';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
// import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { clearPlanner } from '../../operations/planner_operations';
import { UserProgrammeEnrollmentEventType } from '../../API';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const SharedProgrammeOnboardingWizard = () => {
  const { sharedProgrammeId } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  console.log(`location pathname: ${location.pathname}`);

  const today = calendarDayFromJSDate(new Date());

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    sharedProgrammeId,
    {
      skip: !sharedProgrammeId,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const sharedProgrammePersonalisedNutritionEnabled =
    !!sharedProgramme?.personalisedMealScaling;

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: sharedProgrammeEnding } = useGetSharedProgrammeQuery(
    currentProgrammeEnrollment?.sharedProgrammeID,
    {
      skip: !currentProgrammeEnrollment?.sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const [createUserProgrammeEnrollment] =
    useCreateUserProgrammeEnrollmentMutation();

  const [updateUserProgrammeEnrollment] =
    useUpdateUserProgrammeEnrollmentMutation();

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekMutation();

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const isUserEnrolledIntoProgramme = !!currentProgrammeEnrollment;

  const { data: currentSharedProgramme } = useGetSharedProgrammeQuery(
    currentProgrammeEnrollment?.sharedProgrammeID,
    {
      skip: !currentProgrammeEnrollment?.sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const currentProgrammeTitle = currentSharedProgramme?.title;

  const isCompleted = isProgrammeEnrollmentCompleted(
    currentProgrammeEnrollment,
    sharedProgrammeDurationDays(currentSharedProgramme),
    today,
  );

  const isRestartingCurrentProgramme =
    isCompleted && currentSharedProgramme?.id === sharedProgrammeId;

  const [shouldDeleteExistingMeals, setShouldDeleteExistingMeals] = useState(
    !!currentProgrammeTitle && !isRestartingCurrentProgramme,
  );

  const { nutritionConstraints } = sharedProgramme || {};

  const preferredMetrics = deduplicate(
    (nutritionConstraints?.nutritionMetricConstraints || []).map(
      (c) => c.nutritionMetric,
    ),
  ).slice(0, 3);

  const [isLoading, setIsLoading] = useState(false);

  const calorieTarget = currentProgrammeTargetCalories(currentSpaceMembership);

  const targetNutrition = targetDayNutritionFromMetricConstraints(
    nutritionConstraints?.nutritionMetricConstraints || [],
    preferredMetrics,
    calorieTarget,
  );

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [programmeStartDate, setProgrammeStartDate] = useState(
    dayjs().format('YYYY-MM-DD'),
  );

  const allSteps = useMemo(() => {
    const steps = [QUESTION_START_DATE];
    if (sharedProgrammePersonalisedNutritionEnabled) {
      steps.push(CONFIRMATION_MACRO_TARGETS);
    }
    if (isUserEnrolledIntoProgramme && !!currentProgrammeTitle) {
      if (isRestartingCurrentProgramme) {
        steps.unshift(QUESTION_RESTART_CURRENT_PROGRAMME);
      } else {
        steps.unshift(QUESTION_STOP_CURRENT_PROGRAMME);
      }
    }
    return steps;
  }, [
    currentProgrammeTitle,
    isRestartingCurrentProgramme,
    isUserEnrolledIntoProgramme,
    sharedProgrammePersonalisedNutritionEnabled,
  ]);

  const currentStep = allSteps[currentStepIndex];
  const completionPercentage = Math.round(
    (currentStepIndex / allSteps.length) * 100,
  );

  const onChangeProgrammeStartDate = (startDate) => {
    setProgrammeStartDate(startDate);
  };

  const onBack = () => {
    if (currentStepIndex === 0) {
      navigate(-1);
    }
    setCurrentStepIndex(currentStepIndex - 1);
  };

  const plannerViewWeekStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ); */

  const onNext = useCallback(async () => {
    if (currentStepIndex === allSteps.length - 1) {
      const promises = [];
      setIsLoading(true);
      try {
        if (
          currentProgrammeEnrollment &&
          shouldDeleteExistingMeals &&
          sharedProgrammeEnding
        ) {
          const numDaysInProgrammeEnding = programmeTotalDays(
            sharedProgrammeEnding,
          );

          const clearFromDate = dayjs(programmeStartDate).format('YYYY-MM-DD');
          const enrollmentStartDate = programmeStartCalendarDay(
            currentProgrammeEnrollment,
          );
          const clearToDate = dayjs(enrollmentStartDate)
            .add(numDaysInProgrammeEnding, 'day')
            .format('YYYY-MM-DD');

          console.log(
            `Clearing planner from ${clearFromDate} to ${clearToDate}`,
          );
          promises.push(
            clearPlanner(
              clearFromDate,
              clearToDate,
              lazyGetOrCreateDbPlannerWeekByStartDate,
              dispatch,
              deleteMeal,
              deleteNote,
              deleteContentEntry,
              updateDbPlannerWeek,
            ),
          );
        }
        console.log(`Creating enrollment into programme ${sharedProgrammeId}`);
        const spaceOnboardingAnswers =
          currentSpaceMembership?.onboardingAnswers || [];
        const spaceCustomTargetCalories =
          currentSpaceMembership?.customTargetCalories;
        console.log(`Before creating UserProgrammeEnrollment`);
        const now = new Date();
        const enrollDate = calendarDayFromJSDate(now);
        const actualStartDate = programmeStartDate || enrollDate;
        const input = {
          spaceMembershipID: currentSpaceMembership.id,
          sharedProgrammeID: sharedProgrammeId,
          enrolledAt: now.toISOString(),
          onboardingAnswers: spaceOnboardingAnswers,
          customTargetCalories: spaceCustomTargetCalories,
          eventRecords: [
            {
              calendarDay: enrollDate,
              eventType: UserProgrammeEnrollmentEventType.USER_ENROLLED,
              updatedOn: now.toISOString(),
            },
            {
              calendarDay: actualStartDate,
              eventType: UserProgrammeEnrollmentEventType.USER_STARTED,
              updatedOn: now.toISOString(),
            },
          ],
        };
        promises.push(createUserProgrammeEnrollment(input));
        console.log(`New enrollment created`);
        if (currentProgrammeEnrollment) {
          console.log(`Marking previous programme enrollment as ended`);
          const programmeEnrollmentEnding = {
            ...currentProgrammeEnrollment,
            endedAt: new Date().toISOString(),
          };
          promises.push(
            updateUserProgrammeEnrollment(programmeEnrollmentEnding),
          );
        }
        // dispatch(
        //   showGlobalSnackbar({
        //     notificationText: 'Your program is now started',
        //   }),
        // );
        console.log(
          `programmeStartDate = ${programmeStartDate}, plannerViewWeekStartDay = ${plannerViewWeekStartDay}`,
        );
        const viewWeekStartDate = viewWeekStartDateStr(
          programmeStartDate,
          plannerViewWeekStartDay,
        );
        console.log(`viewWeekStartDateStr is ${viewWeekStartDate}`);
        const dayIndex = dayIndexForCalendarDate(programmeStartDate);
        console.log(`Navigating to My Day`);
        navigate(MY_DAY_ROUTE);
        console.log(
          `Navigating to Recommender ${viewWeekStartDate} day ${dayIndex}`,
        );
        navigate(
          formatRoute(END_USER_PLANNER_RECOMMENDER_ROUTE, {
            startDate: viewWeekStartDate,
            dayIndex,
          }),
        );
        await Promise.all(promises);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    setCurrentStepIndex(currentStepIndex + 1);
  }, [
    allSteps.length,
    createUserProgrammeEnrollment,
    currentProgrammeEnrollment,
    currentSpaceMembership?.customTargetCalories,
    currentSpaceMembership.id,
    currentSpaceMembership?.onboardingAnswers,
    currentStepIndex,
    deleteContentEntry,
    deleteMeal,
    deleteNote,
    dispatch,
    lazyGetOrCreateDbPlannerWeekByStartDate,
    navigate,
    plannerViewWeekStartDay,
    programmeStartDate,
    sharedProgrammeEnding,
    sharedProgrammeId,
    shouldDeleteExistingMeals,
    updateDbPlannerWeek,
    updateUserProgrammeEnrollment,
  ]);

  const goal = findAnswerFor(
    QUESTION_GOAL,
    currentSpaceMembership.onboardingAnswers,
  );

  if (currentStep === QUESTION_STOP_CURRENT_PROGRAMME) {
    return (
      <SharedProgrammeOnboardingStepStopCurrentProgramme
        sharedProgrammeTitle={sharedProgramme?.title}
        currentProgrammeTitle={currentProgrammeTitle}
        shouldDeleteExistingMeals={shouldDeleteExistingMeals}
        setShouldDeleteExistingMeals={setShouldDeleteExistingMeals}
        completionPercentage={completionPercentage}
        onNext={onNext}
        onBack={onBack}
      />
    );
  }

  if (currentStep === QUESTION_RESTART_CURRENT_PROGRAMME) {
    return (
      <EndUserSharedProgrammeRestartScreen
        sharedProgrammeTitle={currentSharedProgramme.title}
        completionPercentage={completionPercentage}
        onNext={onNext}
        onBack={onBack}
      />
    );
  }

  if (currentStep === QUESTION_START_DATE) {
    return (
      <SharedProgrammeOnboardingStepStartDate
        sharedProgrammeTitle={sharedProgramme?.title}
        startDate={programmeStartDate}
        setStartDate={onChangeProgrammeStartDate}
        completionPercentage={completionPercentage}
        onNext={onNext}
        onBack={onBack}
        isLoading={isLoading}
      />
    );
  }

  if (currentStep === CONFIRMATION_MACRO_TARGETS) {
    return (
      <SharedProgrammeOnboardingStepConfirmationMacroTargets
        sharedProgrammeTitle={sharedProgramme?.title}
        goal={goal}
        preferredMetrics={preferredMetrics}
        targetCalories={calorieTarget}
        targetNutrition={targetNutrition}
        completionPercentage={completionPercentage}
        onNext={onNext}
        onBack={onBack}
        isLoading={isLoading}
      />
    );
  }

  return null;
};

export default SharedProgrammeOnboardingWizard;
